import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

import { FfsReportRecord, parseReportSummary } from './ffsReportRecord'

const FFS_REPORT_TYPE = RolodexEntryType.FFS_LTA_REPORT

export interface UseFfsPartReportsResult {
  /**
   * Per-indication saved report records, across ALL parts (the governing
   * selector is cross-part best-of, so completion cannot be judged one part at a
   * time). Fully parsed via `parseReportSummary`, so downstream can run the exact
   * same selection as close-out.
   */
  reportsByIndication: ReadonlyMap<string, ReadonlyArray<FfsReportRecord>>
  /**
   * Per-indication count of reports that could not be parsed at all — retained
   * (not dropped) so a malformed sibling blocks its LTA rather than silently
   * letting the step read "assessment recorded" while close-out blocks it.
   */
  parseFailuresByIndication: ReadonlyMap<string, number>
  isLoading: boolean
  forceRefresh: () => Promise<unknown>
}

/**
 * Batch-fetch every `ffs-lta-report` for a set of LTAs (by `indication` tag, all
 * parts) and project them into the fail-soft `FfsReportRecord` shape close-out
 * uses. The step-level counterpart to the per-indication `useSavedReports`.
 *
 * Uses `useRolodexEntriesWithSyncedRefreshes` so the query participates in the
 * workspace's synced-refresh registry. NOTE: the drawer's own report saves go
 * through the plain `useRolodexEntries`-based `useSavedReports`, whose refresh
 * does NOT enter that registry, so callers must still drive `forceRefresh` here
 * off the report-mutation path.
 */
export function useFfsPartReports(
  indicationIds: ReadonlyArray<string>
): UseFfsPartReportsResult {
  // Stable key so a fresh array reference each render doesn't re-issue the query.
  const idsKey = useMemo(
    () => [...indicationIds].sort().join(','),
    [indicationIds]
  )

  const query = useMemo(
    () =>
      idsKey
        ? { type: FFS_REPORT_TYPE, tags: { indication: idsKey.split(',') } }
        : undefined,
    [idsKey]
  )

  const { entries, isLoading, forceRefresh } =
    useRolodexEntriesWithSyncedRefreshes(query)

  const { reportsByIndication, parseFailuresByIndication } = useMemo(() => {
    const byIndication = new Map<string, FfsReportRecord[]>()
    const failures = new Map<string, number>()
    for (const entry of entries as ReadonlyArray<GeckoEntry>) {
      const outcome = parseReportSummary(entry)
      if (outcome.ok) {
        const list = byIndication.get(outcome.summary.indicationId) ?? []
        list.push(outcome.summary)
        byIndication.set(outcome.summary.indicationId, list)
      } else if (outcome.indicationId) {
        failures.set(
          outcome.indicationId,
          (failures.get(outcome.indicationId) ?? 0) + 1
        )
      }
    }
    return {
      reportsByIndication: byIndication as ReadonlyMap<
        string,
        ReadonlyArray<FfsReportRecord>
      >,
      parseFailuresByIndication: failures as ReadonlyMap<string, number>,
    }
  }, [entries])

  return useMemo(
    () => ({
      reportsByIndication,
      parseFailuresByIndication,
      isLoading,
      forceRefresh,
    }),
    [reportsByIndication, parseFailuresByIndication, isLoading, forceRefresh]
  )
}
